import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import {
  downloadCsvFile,
  resolveCsvFileName,
} from "../../utility/CsvReportUtility";
import { printHtmlReportToPdf } from "../../utility/PdfReportUtility";

/**
 * Query parameters for the `/ubt/reports/pip-details` export endpoint.
 */
export interface DownloadPipDetailsArgs {
  /** Authenticated user's NUID (sent as `x-nuid` request header). */
  nuid: string;
  /** Page context identifier, e.g. `"myTeams"` or `"searchProject"`. */
  pageId: string;
  /** Sort column name forwarded directly to the API. */
  sortColumn?: string;
  /** Sort direction. */
  sortOrder?: "ASC" | "DESC";
  /** Region code filter (e.g. `"02"`). */
  regionCode?: string;
  /** Entity / company code filter (e.g. `"All"` or `"01"`). */
  entityCode?: string;
  /** Area code filter. */
  areaCode?: string;
  /** Service area code filter. */
  serviceAreaCode?: string;
  /** Location code filter. */
  locationCode?: string;
  /** Comma-separated department IDs, or `"All"`. */
  deptIds?: string;
  /** Tilde-delimited focus area IDs, or `"All"`. */
  focusAreas?: string;
  /** Tilde-delimited value-compass IDs, or `"All"`. */
  valueCompassIds?: string;
  /** Tilde-delimited metric IDs, or `"All"`. */
  metrics?: string;
  /** Tilde-delimited result IDs, or `"All"`. */
  resultIds?: string;
  /** Tilde-delimited test-of-change rating IDs, or `"All"`. */
  tocRatingIds?: string;
  /** Project performance status filter, e.g. `"active"`, `"complete"`, `"both"`. */
  perfStatus?: string;
  /** Project start date filter (YYYY-MM-DD format). */
  startDate?: string;
  /** Project end date filter (YYYY-MM-DD format). */
  endDate?: string;
  /** Minimum ROI filter. */
  minRoi?: string;
  /** Maximum ROI filter. */
  maxRoi?: string;
  /** Project name search string. */
  searchByProject?: string;
  /** Search criterion selected on Search Projects. */
  searchBy?: string;
  /** Search value entered on Search Projects. */
  searchString?: string;
  /** Cost center number when search-by type is CostCenterNumber. */
  costCenter?: string;
  /** UBT ID range filters used by Search Projects. */
  fromId?: string;
  toId?: string;
  /** Comma-separated team IDs to filter the report to specific teams. */
  teamIds?: string | null;
}

/**
 * Arguments for printing a project record PDF from
 * `/ubt/reports/project-record-pdf/{perfId}`.
 */
export interface DownloadProjectRecordPdfArgs {
  /** The `perfId` of the project whose record should be printed. */
  perfId: string | number;
  /** Authenticated user's NUID (sent as `x-nuid` request header). */
  nuid: string;
}

/**
 * Arguments for printing the project test-of-change HTML report from
 * `GET /ubt/reports/project/:projectId/test-of-change`.
 */
export interface DownloadTestOfChangePdfArgs {
  /** The `perfId` of the project whose test-of-change report should be printed. */
  projectId: string | number;
  /** Display name of the project, URL-encoded and sent as `projectName` query param. */
  projectName: string;
  /** Column name to sort the test-of-change table by. */
  sortColumn: string;
  /** Sort direction forwarded directly to the API. */
  sortOrder: "ASC" | "DESC";
}

/**
 * Query parameters accepted by the test-of-change report endpoint.
 * Every field is optional; only non-empty values are forwarded to the API.
 */
export interface TestOfChangeReportParams {
  reportIdentifier?: string;
  pageId?: string;
  regionCd?: string;
  entityCd?: string;
  areaCd?: string;
  centerCd?: string;
  locationCd?: string;
  costCenterCd?: string;
  focusAreaCd?: string;
  valueCompassStaticCd?: string;
  metricCd?: string;
  resultCd?: string;
  tocRatingCd?: string;
  teamIdsCd?: string;
  pipSelected?: string;
  searchString?: string;
  queryBy?: string;
  searchProjectString?: string;
  includeProjectType?: string;
  sortColumn?: string;
  sortOrder?: string;
  minRoi?: string;
  maxRoi?: string;
  startDate?: string;
  endDate?: string;
  fromId?: string;
  toId?: string;
}

/** Shape of the `reportSlice` state tree. */
export interface ReportSliceState {
  /** True while the CSV export API call is in progress. */
  loading: boolean;
  /** Error message from the last failed export, or null if none. */
  error: string | null;
  /** True after the last export completed successfully. */
  success: boolean;
  /** File name of the most recently downloaded CSV report. */
  lastDownloadedFileName: string | null;
  /** True while a project record PDF is being fetched and printed. */
  pdfLoading: boolean;
  /** Error message from the last failed PDF download, or null. */
  pdfError: string | null;
  /** True while the test-of-change HTML report is being fetched and printed. */
  tocPdfLoading: boolean;
  /** Error message from the last failed test-of-change PDF print, or null. */
  tocPdfError: string | null;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const DEFAULT_FILENAME = "TEST_OF_CHANGE.csv";

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Extracts a human-readable error message from any thrown value.
 * @param error - The caught error value.
 * @param fallback - Message to use when the error provides no detail.
 * @returns A string suitable for display to the user.
 */
const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string") return error;
  const err = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return err?.response?.data?.message ?? err?.message ?? fallback;
};

// ── Thunks ────────────────────────────────────────────────────────────────────

/**
 * Exports the test-of-change report as CSV and triggers a browser file download.
 *
 * All query parameters are optional. Empty, null, and undefined values are
 * stripped before the request is sent so the API only receives meaningful data.
 *
 * @param params - Optional filter and sort parameters for the report.
 * @returns The resolved CSV file name on success.
 */
export const exportTestOfChangeCSV = createAsyncThunk<
  { fileName: string },
  TestOfChangeReportParams,
  { rejectValue: string }
>(
  "report/exportTestOfChangeCSV",
  async (params, { rejectWithValue }) => {
    try {
      // Strip undefined, null, and whitespace-only values
      const queryParams = Object.fromEntries(
        Object.entries(params).filter(
          ([, v]) => v !== undefined && v !== null && String(v).trim() !== "",
        ),
      ) as Record<string, string>;

      const response = await api.get(endpoints.ubt.reports.testOfChangeReport, {
        params: queryParams,
        headers: { accept: "text/csv" },
        responseType: "blob",
      });

      const fileName = resolveCsvFileName(
        response.headers?.["content-disposition"] as string | undefined,
        DEFAULT_FILENAME,
      );

      downloadCsvFile(response.data as Blob, fileName);

      return { fileName };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to export Test of Change CSV"),
      );
    }
  },
);

/**
 * Downloads the PIP Details CSV report and triggers a browser file download.
 *
 * For the My Teams page only three params are required (`pageId`, `sortColumn`,
 * `sortOrder`).  Other pages may supply additional filter params.
 *
 * @param args - Page context, sort params, and authenticated user's NUID.
 * @returns The resolved CSV file name on success.
 */
export const downloadPipDetailsReport = createAsyncThunk<
  { fileName: string },
  DownloadPipDetailsArgs,
  { rejectValue: string }
>(
  "report/downloadPipDetailsReport",
  async (
    {
      nuid,
      pageId,
      sortColumn,
      sortOrder,
      regionCode,
      entityCode,
      areaCode,
      serviceAreaCode,
      locationCode,
      deptIds,
      focusAreas,
      valueCompassIds,
      metrics,
      resultIds,
      tocRatingIds,
      perfStatus,
      startDate,
      endDate,
      minRoi,
      maxRoi,
      searchByProject,
      searchBy,
      searchString,
      costCenter,
      fromId,
      toId,
      teamIds
    },
    { rejectWithValue },
  ) => {
    try {
      // Build optional params in a single pass — avoids repeated if-guard complexity.
      const optionals: Record<string, string | null | undefined> = {
        sortColumn, sortOrder, regionCode, entityCode, areaCode,
        serviceAreaCode, locationCode, deptIds, focusAreas, valueCompassIds,
        metrics, resultIds, tocRatingIds, perfStatus, startDate, endDate,
        minRoi, maxRoi, searchByProject, searchBy, searchString, costCenter,
        fromId, toId,
        teamIds: teamIds ?? undefined,
      };
      const params: Record<string, string> = { pageId };
      Object.entries(optionals).forEach(([key, val]) => {
        if (val) params[key] = val;
      });

      const response = await api.get(endpoints.ubt.reports.pipDetails, {
        params,
        headers: { accept: "text/csv", "x-nuid": nuid },
        responseType: "blob",
      });

      const fileName = resolveCsvFileName(
        response.headers?.["content-disposition"] as string | undefined,
        "PIP_DETAILS.csv",
      );

      downloadCsvFile(response.data as Blob, fileName);
      return { fileName };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to download PIP Details CSV"),
      );
    }
  },
);

/**
 * Fetches the project test-of-change HTML report from the API and opens the
 * browser print dialog so the user can save it as PDF.
 *
 * Calls `GET /ubt/reports/project/:projectId/test-of-change` with
 * `sortColumn`, `sortOrder`, and `projectName` as query parameters.
 *
 * @param args - The project ID, display name, sort column, and sort direction.
 * @returns A plain success marker on resolution.
 */
export const downloadTestOfChangePDF = createAsyncThunk<
  { success: true },
  DownloadTestOfChangePdfArgs,
  { rejectValue: string }
>(
  "report/downloadTestOfChangePDF",
  async ({ projectId, projectName, sortColumn, sortOrder }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.ubt.reports.projectTestOfChangePdf(projectId),
        {
          params: {
            sortColumn,
            sortOrder,
            projectName,
          },
          headers: { accept: "application/json" },
          responseType: "blob",
        },
      );

      await printHtmlReportToPdf(response.data as Blob);
      return { success: true as const };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to print Test of Change PDF"),
      );
    }
  },
);

/**
 * Fetches the project record HTML from the API and opens the browser print
 * dialog so the user can save it as PDF.
 *
 * The back-end returns a JSON blob with an `htmlContent` field that is
 * rendered in a new window and printed via `window.print()`.
 *
 * @param args - The `perfId` of the selected project and the user's NUID.
 * @returns A plain success marker on resolution.
 */
export const downloadProjectRecordPDF = createAsyncThunk<
  { success: true },
  DownloadProjectRecordPdfArgs,
  { rejectValue: string }
>(
  "report/downloadProjectRecordPDF",
  async ({ perfId, nuid }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.ubt.reports.projectRecordPdf(perfId),
        {
          headers: { accept: "application/json", "x-nuid": nuid },
          responseType: "blob",
        },
      );

      await printHtmlReportToPdf(response.data as Blob);
      return { success: true as const };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to print project record PDF"),
      );
    }
  },
);

// ── Initial state ─────────────────────────────────────────────────────────────

const initialState: ReportSliceState = {
  loading: false,
  error: null,
  success: false,
  lastDownloadedFileName: null,
  pdfLoading: false,
  pdfError: null,
  tocPdfLoading: false,
  tocPdfError: null,
};

// ── Slice ─────────────────────────────────────────────────────────────────────

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    /**
     * Resets all report export state (loading, error, success, lastDownloadedFileName,
     * pdfLoading, pdfError).
     * Dispatch this after the user dismisses a success or error notification.
     */
    clearReportState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.lastDownloadedFileName = null;
      state.pdfLoading = false;
      state.pdfError = null;
      state.tocPdfLoading = false;
      state.tocPdfError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(exportTestOfChangeCSV.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(exportTestOfChangeCSV.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.success = true;
        state.lastDownloadedFileName = action.payload.fileName;
      })
      .addCase(exportTestOfChangeCSV.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ??
          action.error.message ??
          "Failed to export Test of Change CSV";
        state.success = false;
      })
      // ── downloadPipDetailsReport ──────────────────────────────────────────
      .addCase(downloadPipDetailsReport.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(downloadPipDetailsReport.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.success = true;
        state.lastDownloadedFileName = action.payload.fileName;
      })
      .addCase(downloadPipDetailsReport.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ??
          action.error.message ??
          "Failed to download PIP Details CSV";
        state.success = false;
      })
      // ── downloadProjectRecordPDF ──────────────────────────────────────────
      .addCase(downloadProjectRecordPDF.pending, (state) => {
        state.pdfLoading = true;
        state.pdfError = null;
      })
      .addCase(downloadProjectRecordPDF.fulfilled, (state) => {
        state.pdfLoading = false;
        state.pdfError = null;
      })
      .addCase(downloadProjectRecordPDF.rejected, (state, action) => {
        state.pdfLoading = false;
        state.pdfError =
          action.payload ??
          action.error.message ??
          "Failed to print project record PDF";
      })
      // ── downloadTestOfChangePDF ───────────────────────────────────────────
      .addCase(downloadTestOfChangePDF.pending, (state) => {
        state.tocPdfLoading = true;
        state.tocPdfError = null;
      })
      .addCase(downloadTestOfChangePDF.fulfilled, (state) => {
        state.tocPdfLoading = false;
        state.tocPdfError = null;
      })
      .addCase(downloadTestOfChangePDF.rejected, (state, action) => {
        state.tocPdfLoading = false;
        state.tocPdfError =
          action.payload ??
          action.error.message ??
          "Failed to print Test of Change PDF";
      });
  },
});

// ── Actions ───────────────────────────────────────────────────────────────────

export const { clearReportState } = reportSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────────

type ReportRootState = { reportSlice: ReportSliceState };

/**
 * Selects whether the test-of-change CSV export is in progress.
 * @param state - The Redux root state.
 */
export const selectReportLoading = (state: ReportRootState): boolean =>
  state.reportSlice?.loading ?? false;

/**
 * Selects the report export error message, or null if there is no error.
 * @param state - The Redux root state.
 */
export const selectReportError = (state: ReportRootState): string | null =>
  state.reportSlice?.error ?? null;

/**
 * Selects whether the last report export completed successfully.
 * @param state - The Redux root state.
 */
export const selectReportSuccess = (state: ReportRootState): boolean =>
  state.reportSlice?.success ?? false;

/**
 * Selects the file name of the most recently downloaded report.
 * @param state - The Redux root state.
 */
export const selectLastDownloadedFileName = (
  state: ReportRootState,
): string | null => state.reportSlice?.lastDownloadedFileName ?? null;

/**
 * Selects whether a project record PDF is currently being fetched / printed.
 * @param state - The Redux root state.
 */
export const selectProjectPdfLoading = (state: ReportRootState): boolean =>
  state.reportSlice?.pdfLoading ?? false;

/**
 * Selects the error message from the last failed project record PDF download.
 * @param state - The Redux root state.
 */
export const selectProjectPdfError = (state: ReportRootState): string | null =>
  state.reportSlice?.pdfError ?? null;

/**
 * Selects whether the test-of-change PDF report is currently being fetched / printed.
 * @param state - The Redux root state.
 */
export const selectTocPdfLoading = (state: ReportRootState): boolean =>
  state.reportSlice?.tocPdfLoading ?? false;

/**
 * Selects the error message from the last failed test-of-change PDF print.
 * @param state - The Redux root state.
 */
export const selectTocPdfError = (state: ReportRootState): string | null =>
  state.reportSlice?.tocPdfError ?? null;

export default reportSlice.reducer;
